import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportData, ReportMeta, SortDir } from './types';

interface State {
  meta: ReportMeta | null;
  data: ReportData | null;
  loading: boolean;
  error: string;
  page: number;
  sort?: string;
  dir?: SortDir;
  search: string;
}

const tz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

/**
 * Drives a single report: loads column metadata once, then (re)fetches a page of
 * data whenever page / sort / debounced search change. Uses the app's plain fetch
 * (auth token is injected globally in App.tsx).
 */
export function useReportData(apiUrl: string, reportId: string | null) {
  const [s, setS] = useState<State>({
    meta: null, data: null, loading: false, error: '', page: 1, search: '',
  });
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load metadata + reset view when the report changes.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    setS((p) => ({ ...p, meta: null, data: null, error: '', page: 1, search: '', sort: undefined, dir: undefined }));
    (async () => {
      try {
        const r = await fetch(`${apiUrl}/admin/reports/${encodeURIComponent(reportId)}`);
        const meta = await r.json();
        if (!r.ok) throw new Error(meta?.error || 'Failed to load report');
        if (!cancelled) {
          setS((p) => ({ ...p, meta, sort: meta.defaultSort?.column, dir: meta.defaultSort?.dir }));
        }
      } catch (e: any) {
        if (!cancelled) setS((p) => ({ ...p, error: e.message || 'Failed to load report' }));
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, reportId]);

  const fetchData = useCallback(
    async (page: number, sort: string | undefined, dir: SortDir | undefined, search: string, pageSize: number) => {
      if (!reportId) return;
      setS((p) => ({ ...p, loading: true, error: '' }));
      try {
        const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (sort) params.set('sort', sort);
        if (dir) params.set('dir', dir);
        if (search.trim()) params.set('q', search.trim());
        const t = tz();
        if (t) params.set('tz', t);
        const r = await fetch(`${apiUrl}/admin/reports/${encodeURIComponent(reportId)}/data?${params}`);
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Failed to run report');
        setS((p) => ({ ...p, data, loading: false }));
      } catch (e: any) {
        setS((p) => ({ ...p, error: e.message || 'Failed to run report', loading: false }));
      }
    },
    [apiUrl, reportId],
  );

  // Fetch data once meta is known and whenever page/sort/search change.
  useEffect(() => {
    if (!s.meta) return;
    fetchData(s.page, s.sort, s.dir, s.search, s.meta.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.meta, s.page, s.sort, s.dir, s.search]);

  const setSort = useCallback((key: string) => {
    setS((p) => {
      const dir: SortDir = p.sort === key && p.dir === 'desc' ? 'asc' : 'desc';
      return { ...p, sort: key, dir, page: 1 };
    });
  }, []);

  const setPage = useCallback((page: number) => setS((p) => ({ ...p, page })), []);

  const setSearch = useCallback((search: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setS((p) => ({ ...p, search, page: 1 }));
    }, 300);
  }, []);

  return {
    meta: s.meta,
    data: s.data,
    loading: s.loading,
    error: s.error,
    page: s.page,
    sort: s.sort,
    dir: s.dir,
    search: s.search,
    setSort,
    setPage,
    setSearch,
    tz,
  };
}
